/**
 * Prekey System Implementation
 * Allows asynchronous message delivery without requiring both parties online
 * Based on Signal Protocol's prekey system
 */

import nacl from 'tweetnacl';
import * as naclUtil from 'tweetnacl-util';

export interface Prekey {
  keyId: number;
  publicKey: Uint8Array;
}

export interface PrekeyBundle {
  identityKey: Uint8Array; // Long-term identity key (Ed25519 public key)
  signedPrekey: {
    keyId: number;
    publicKey: Uint8Array; // Curve25519 public key
    signature: Uint8Array; // Signed by identity key (Ed25519)
  };
  oneTimePrekeys: Prekey[]; // Array of one-time prekeys (Curve25519)
}

/**
 * Generate a prekey bundle for a user
 * @param identityKeyPair User's long-term identity key pair (Ed25519 signing key pair)
 * @param numOneTimePrekeys Number of one-time prekeys to generate
 */
export function generatePrekeyBundle(
  identityKeyPair: nacl.SignKeyPair,
  numOneTimePrekeys: number = 100
): PrekeyBundle {
  // Validate Ed25519 identity key sizes
  if (identityKeyPair.publicKey.length !== 32) {
    throw new Error(`Invalid Ed25519 public key size: ${identityKeyPair.publicKey.length} bytes (expected 32)`);
  }
  if (identityKeyPair.secretKey.length !== 64) {
    throw new Error(`Invalid Ed25519 secret key size: ${identityKeyPair.secretKey.length} bytes (expected 64). This may indicate old Curve25519 keys are still stored. Please clear localStorage and reinitialize.`);
  }
  
  // Additional validation: ensure secret key structure is correct for Ed25519
  // Ed25519 secret keys are 64 bytes: first 32 bytes are the seed, last 32 bytes are the public key
  const secretKeyPublicPart = identityKeyPair.secretKey.slice(32, 64);
  let keysMatch = true;
  if (secretKeyPublicPart.length !== identityKeyPair.publicKey.length) {
    keysMatch = false;
  } else {
    for (let i = 0; i < secretKeyPublicPart.length; i++) {
      if (secretKeyPublicPart[i] !== identityKeyPair.publicKey[i]) {
        keysMatch = false;
        break;
      }
    }
  }
  if (!keysMatch) {
    throw new Error(`Ed25519 secret key validation failed: public key mismatch. The secret key may be corrupted or from an old format.`);
  }
  
  // Generate signed prekey (Curve25519 for encryption)
  const signedPrekeyKeyPair = nacl.box.keyPair();
  // Use modulo to ensure ID fits in PostgreSQL INTEGER (max: 2,147,483,647)
  const signedPrekeyId = Date.now() % 2147483647;
  
  // Sign signed prekey with identity key (Ed25519 secret key)
  // nacl.sign.detached requires a 64-byte Ed25519 secret key
  // The secret key must be the full 64-byte Ed25519 secret key, not a Curve25519 key
  let signature: Uint8Array;
  try {
    signature = nacl.sign.detached(
      signedPrekeyKeyPair.publicKey,
      identityKeyPair.secretKey
    );
  } catch (error: any) {
    if (error.message?.includes('bad secret key size')) {
      throw new Error(`Failed to sign prekey: ${error.message}. The identity key pair may be corrupted or in the wrong format (expected Ed25519, got Curve25519?). Please clear localStorage and reinitialize encryption.`);
    }
    throw error;
  }
  
  // Generate one-time prekeys
  const oneTimePrekeys: Prekey[] = [];
  const baseTimestamp = Date.now() % 2147483647; // Ensure base fits in INTEGER range
  for (let i = 0; i < numOneTimePrekeys; i++) {
    const prekeyPair = nacl.box.keyPair();
    // Use modulo to ensure ID fits in PostgreSQL INTEGER (max: 2,147,483,647)
    // Add i and wrap around if needed
    const keyId = (baseTimestamp + i) % 2147483647;
    oneTimePrekeys.push({
      keyId: keyId || 1, // Ensure non-zero (0 would be invalid)
      publicKey: prekeyPair.publicKey
    });
  }
  
  return {
    identityKey: identityKeyPair.publicKey,
    signedPrekey: {
      keyId: signedPrekeyId,
      publicKey: signedPrekeyKeyPair.publicKey,
      signature
    },
    oneTimePrekeys
  };
}

/**
 * Perform 3-DH (Triple Diffie-Hellman) key exchange
 * Establishes shared secret between two parties
 * Note: Identity keys are Ed25519 (for signing), but encryption uses Curve25519
 * We need to convert Ed25519 identity key to Curve25519 for DH operations
 */
export function perform3DH(
  localIdentityKeyPair: nacl.SignKeyPair,
  localEphemeralKeyPair: nacl.BoxKeyPair,
  remoteIdentityKey: Uint8Array,
  remoteSignedPrekey: Uint8Array,
  remoteOneTimePrekey?: Uint8Array
): Uint8Array {
  // Convert Ed25519 identity key to Curve25519 for encryption
  // Ed25519 and Curve25519 use the same curve, so we can convert
  const localIdentityBoxKeyPair = nacl.box.keyPair.fromSecretKey(
    localIdentityKeyPair.secretKey.slice(0, 32) // Use first 32 bytes (Curve25519 secret key)
  );
  
  // Convert remote Ed25519 identity key to Curve25519 public key
  // For Ed25519 -> Curve25519 conversion, we use the public key directly
  // (Ed25519 and Curve25519 public keys are compatible on the same curve)
  const remoteIdentityBoxKey = remoteIdentityKey; // Ed25519 and Curve25519 public keys are compatible
  
  // DH1: local identity key + remote signed prekey
  const dh1 = nacl.box.before(remoteSignedPrekey, localIdentityBoxKeyPair.secretKey);
  
  // DH2: local ephemeral key + remote identity key
  const dh2 = nacl.box.before(remoteIdentityBoxKey, localEphemeralKeyPair.secretKey);
  
  // DH3: local ephemeral key + remote signed prekey
  const dh3 = nacl.box.before(remoteSignedPrekey, localEphemeralKeyPair.secretKey);
  
  // DH4: local ephemeral key + remote one-time prekey (if available)
  let dh4: Uint8Array | null = null;
  if (remoteOneTimePrekey) {
    dh4 = nacl.box.before(remoteOneTimePrekey, localEphemeralKeyPair.secretKey);
  }
  
  // Combine all DH outputs
  const combined = new Uint8Array(
    (dh1?.length || 0) +
    (dh2?.length || 0) +
    (dh3?.length || 0) +
    (dh4?.length || 0)
  );
  
  let offset = 0;
  if (dh1) {
    combined.set(dh1, offset);
    offset += dh1.length;
  }
  if (dh2) {
    combined.set(dh2, offset);
    offset += dh2.length;
  }
  if (dh3) {
    combined.set(dh3, offset);
    offset += dh3.length;
  }
  if (dh4) {
    combined.set(dh4, offset);
  }
  
  // Hash combined output to get shared secret
  return nacl.hash(combined).slice(0, 32); // Use first 32 bytes
}

/**
 * Verify signed prekey signature
 * @param signedPrekey Curve25519 public key that was signed
 * @param signature Ed25519 signature
 * @param identityKey Ed25519 public key (identity key)
 */
export function verifySignedPrekey(
  signedPrekey: Uint8Array,
  signature: Uint8Array,
  identityKey: Uint8Array
): boolean {
  return nacl.sign.detached.verify(
    signedPrekey,
    signature,
    identityKey
  );
}

/**
 * Serialize prekey bundle for storage/transmission
 */
export function serializePrekeyBundle(bundle: PrekeyBundle): string {
  return JSON.stringify({
    identityKey: naclUtil.encodeBase64(bundle.identityKey),
    signedPrekey: {
      keyId: bundle.signedPrekey.keyId,
      publicKey: naclUtil.encodeBase64(bundle.signedPrekey.publicKey),
      signature: naclUtil.encodeBase64(bundle.signedPrekey.signature)
    },
    oneTimePrekeys: bundle.oneTimePrekeys.map(pk => ({
      keyId: pk.keyId,
      publicKey: naclUtil.encodeBase64(pk.publicKey)
    }))
  });
}

/**
 * Deserialize prekey bundle
 */
export function deserializePrekeyBundle(serialized: string): PrekeyBundle {
  const data = JSON.parse(serialized);
  return {
    identityKey: naclUtil.decodeBase64(data.identityKey),
    signedPrekey: {
      keyId: data.signedPrekey.keyId,
      publicKey: naclUtil.decodeBase64(data.signedPrekey.publicKey),
      signature: naclUtil.decodeBase64(data.signedPrekey.signature)
    },
    oneTimePrekeys: data.oneTimePrekeys.map((pk: any) => ({
      keyId: pk.keyId,
      publicKey: naclUtil.decodeBase64(pk.publicKey)
    }))
  };
}

